import type { AxisScale } from '@visx/xychart';

import { DOMAIN_EFFECTIVELY_INFINITE_THRESHOLD } from '../constants';
import type { ChartDataPoint } from '../types';

/**
 * Utility functions for working with chart scales, especially handling
 * the difference between band scales (string domain) and continuous scales (numeric domain).
 */

/**
 * Gets the appropriate input value to pass to xScale based on scale type.
 *
 * For band scales, the domain is ALWAYS string[], so we need to convert numeric values to strings.
 * For time/linear scales, we pass numbers directly.
 * We will have to extend this to handle ordinal and point scales when we add them.
 *
 * @param point - The numeric x-value from data
 * @param isBandScaleType - Whether the scale is a band scale (true) or continuous scale (false)
 * @returns The value to pass to xScale (string for band scales, number otherwise)
 */
export function getScaleInput(point: number, isBandScaleType: boolean): string | number {
  // For band scales, ALWAYS convert numeric point to string
  // Band scale domain is always string[] (e.g., ['1704067200000', ...])
  if (isBandScaleType) {
    return String(point);
  }

  // For time/linear scales, return number
  return point;
}

/**
 * Checks if a point is within the zoom domain.
 *
 * Handles cross-scale-type zoom domains for synced charts:
 * - LineChart (time/linear) can receive string[] from synced ColumnChart
 * - ColumnChart (band) can receive [number, number] from synced LineChart
 *
 * @param point - The numeric x-value to check
 * @param xScaleType - The type of x-scale ('band', 'time', or 'linear')
 * @param zoomDomain - The current zoom domain (string[] for band scales, [number, number] for time/linear, or null)
 * @returns true if point is within zoom domain (or if not zoomed), false otherwise
 */
export function isPointInZoomDomain(
  point: number,
  xScaleType: 'band' | 'time' | 'linear',
  zoomDomain: [number, number] | string[] | null,
): boolean {
  if (!zoomDomain) {
    // Not zoomed, point is always "in domain"
    return true;
  }

  // Determine the domain type from the actual zoomDomain value
  // (may differ from xScaleType in synced charts with different scale types)
  const isStringDomain = Array.isArray(zoomDomain) && zoomDomain.length > 0 && typeof zoomDomain[0] === 'string';
  const isNumericDomain = Array.isArray(zoomDomain) && zoomDomain.length === 2 && typeof zoomDomain[0] === 'number';

  if (xScaleType === 'band') {
    // Band scale chart
    if (isStringDomain) {
      // Normal case: band scale with string[] domain
      const stringDomain = zoomDomain as string[];
      return stringDomain.includes(String(point));
    }
    if (isNumericDomain) {
      // Cross-sync case: band scale receiving [number, number] from continuous scale chart
      const [minX, maxX] = zoomDomain as [number, number];
      const actualMin = Math.min(minX, maxX);
      const actualMax = Math.max(minX, maxX);
      return point >= actualMin && point <= actualMax;
    }
  } else {
    // Time/linear scale chart
    if (isNumericDomain) {
      // Normal case: continuous scale with [number, number] domain
      const [minX, maxX] = zoomDomain as [number, number];
      const actualMin = Math.min(minX, maxX);
      const actualMax = Math.max(minX, maxX);
      return point >= actualMin && point <= actualMax;
    }
    if (isStringDomain) {
      // Cross-sync case: continuous scale receiving string[] from band scale chart
      // Parse string values to numbers and use min/max as range
      const stringDomain = zoomDomain as string[];
      const numericValues = stringDomain.map(Number).filter((n) => !Number.isNaN(n));
      if (numericValues.length > 0) {
        const actualMin = Math.min(...numericValues);
        const actualMax = Math.max(...numericValues);
        return point >= actualMin && point <= actualMax;
      }
    }
  }

  // Fallback: if we can't determine, assume it's in domain
  return true;
}

/**
 * Checks if a string category point is within the zoom domain.
 * For band scales with categorical data (string x-values).
 *
 * @param category - The string category to check
 * @param zoomDomain - The current zoom domain (string[] for band scales, or null)
 * @returns true if category is in zoom domain (or if not zoomed), false otherwise
 */
export function isStringCategoryInZoomDomain(
  category: string,
  zoomDomain: [number, number] | string[] | null,
): boolean {
  if (!zoomDomain) {
    return true; // Not zoomed, always visible
  }

  // Band scale with string domain - check if category is in the array
  if (Array.isArray(zoomDomain) && zoomDomain.length > 0 && typeof zoomDomain[0] === 'string') {
    return (zoomDomain as string[]).includes(category);
  }

  // Numeric domain from synced continuous scale chart - can't determine visibility
  // for string categories, so allow rendering (scale will handle it)
  return true;
}

/**
 * Type guard to check if a scale is a band scale (has bandwidth and domain methods).
 * Band scales are used for categorical data (e.g., column charts).
 */
export function isBandScale(scale: AxisScale): boolean {
  const scaleWithBandwidth = scale as unknown as { bandwidth?: () => number; domain?: () => unknown[] };
  const hasBandwidth = typeof scaleWithBandwidth.bandwidth === 'function';
  const hasDomain = typeof scaleWithBandwidth.domain === 'function';
  return hasBandwidth && hasDomain;
}

/**
 * Select evenly spaced values from an array while always including first and last.
 * Returns at most `maxCount` values with even spacing across the array.
 *
 * @param values - Array of values to select from
 * @param maxCount - Maximum number of values to return
 * @returns Array of evenly spaced values
 */
export function selectEvenlySpacedValues<T>(values: T[], maxCount: number): T[] {
  const len = values.length;

  if (len <= maxCount || maxCount <= 0) {
    return values;
  }

  if (maxCount === 1) {
    return [values[0]];
  }

  if (maxCount === 2) {
    return [values[0], values[len - 1]];
  }

  const result: T[] = [];
  for (let i = 0; i < maxCount; i++) {
    const index = Math.round((i * (len - 1)) / (maxCount - 1));
    result.push(values[index]);
  }
  return result;
}

/**
 * Extract numeric domain bounds from visx scale domain.
 * Handles both Date objects (time scales) and numbers (linear scales).
 *
 * @param domain - Scale domain tuple from visx
 * @returns Numeric bounds [min, max] or null if invalid
 */
export function extractDomainBounds(domain: [number | Date, number | Date]): [number, number] | null {
  if (domain.length !== 2) {
    return null;
  }

  const min = domain[0] instanceof Date ? domain[0].getTime() : domain[0];
  const max = domain[1] instanceof Date ? domain[1].getTime() : domain[1];

  if (typeof min !== 'number' || typeof max !== 'number') {
    return null;
  }

  return [min, max];
}

/** Returns true for Infinity, -Infinity, or values with magnitude >= DOMAIN_EFFECTIVELY_INFINITE_THRESHOLD (API sentinels like Number.MAX_VALUE). */
export function isEffectivelyInfinite(value: number): boolean {
  return (
    value === Infinity ||
    value === -Infinity ||
    (typeof value === 'number' && Math.abs(value) >= DOMAIN_EFFECTIVELY_INFINITE_THRESHOLD)
  );
}

/**
 * Computes the minimum and maximum Y values across specified data keys in chart data.
 * Optionally includes additional Y values (e.g., from reference lines) in the domain calculation.
 * Infinity/-Infinity and effectively infinite values (|v| >= 1e15) are excluded; AreaSeries handles them.
 *
 * @param data - Array of chart data points
 * @param dataKeys - Array of data keys to consider for domain calculation
 * @param additionalYValues - Optional array of additional Y values to include in domain (e.g., from reference lines)
 * @param domain - Optional soft domain anchors. When provided, the computed domain expands to include
 *   domain.min and/or domain.max without clipping actual data values.
 * @returns Tuple of [min, max] Y values, or [0, 0] if no valid data
 *
 * @example
 * ```ts
 * const data = [{ x: 1, latency: 100, errors: 5 }, { x: 2, latency: 200, errors: 10 }];
 * const [min, max] = computeYDomain(data, ['latency', 'errors'], [150]);
 * // Returns: [5, 200] (includes the additional value 150 in calculation)
 *
 * // With soft domain anchors:
 * const [min2, max2] = computeYDomain(data, ['latency'], undefined, { min: 0 });
 * // Returns: [0, 200] (domain expanded to include 0)
 * ```
 */
export function computeYDomain<T extends Record<string, unknown>>(
  data: T[] | null | undefined,
  dataKeys: string[],
  additionalYValues?: number[],
  domain?: { min?: number; max?: number },
): [number, number] {
  let min = Infinity;
  let max = -Infinity;

  // Compute min/max from data keys
  if (data && data.length > 0 && dataKeys.length > 0) {
    for (const point of data) {
      for (const dataKey of dataKeys) {
        const value = point[dataKey];
        if (typeof value === 'number' && Number.isFinite(value) && !isEffectivelyInfinite(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
    }
  }

  // Include additional Y values (e.g., from reference lines) - exclude non-finite (reference lines are always finite)
  if (additionalYValues && additionalYValues.length > 0) {
    for (const value of additionalYValues) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }
  }

  // Apply soft domain anchors: expand (never shrink) the domain to include these bounds.
  // Applied before the empty-data guard so anchors establish a base domain even with no data.
  if (domain?.min !== undefined) {
    min = Math.min(min, domain.min);
  }
  if (domain?.max !== undefined) {
    max = Math.max(max, domain.max);
  }

  // If no valid values were found (and no domain anchors provided), return [0, 0]
  if (min === Infinity || max === -Infinity) {
    return [0, 0];
  }

  return [min, max];
}

/**
 * Get sorted unique x-axis values from chart data within the given bounds.
 * Handles both numeric and Date values, and filters out null/undefined values.
 * When bounds are provided, only includes x values within the bounds (for zoomed views).
 *
 * @param chartData - Array of chart data points
 * @param dataKey - Key to extract x values from data points
 * @param bounds - Optional bounds to filter values (for zoomed views)
 * @returns Sorted array of unique numeric x values
 */
export function getUniqueXValues(
  chartData: ChartDataPoint[] | null | undefined,
  dataKey: string,
  bounds?: [number, number] | null,
): number[] {
  if (!chartData || chartData.length === 0 || !dataKey) {
    return [];
  }

  const uniqueValues = new Set<number>();
  const [minBound, maxBound] = bounds ?? [null, null];

  for (const datum of chartData) {
    const xValue = datum[dataKey];
    if (xValue !== null && xValue !== undefined && typeof xValue === 'number' && !Number.isNaN(xValue)) {
      // If bounds are provided, only include values within the bounds (zoomed domain)
      if (minBound === null || maxBound === null || (xValue >= minBound && xValue <= maxBound)) {
        uniqueValues.add(xValue);
      }
    }
  }

  // Return sorted array of unique values
  return Array.from(uniqueValues).sort((a, b) => a - b);
}

/**
 * Calculates nice round boundaries and explicit tick values for a numeric domain.
 * Ensures ticks ALWAYS include the min and max boundaries for proper grid line alignment.
 *
 * @param min - Actual data minimum
 * @param max - Actual data maximum
 * @param targetTicks - Target number of ticks (approximate, minimum 2)
 * @param referenceLinePoints - Optional array of horizontal reference line Y values.
 *   When a reference line point sits on (or very near) a domain boundary, the domain
 *   is extended by one tick step so the line isn't invisible at the edge.
 * @param constraints - Optional hard bounds that the niced domain must not cross.
 *   `hardMin`: the domain will not go below this value (e.g. 0 for non-negative data).
 *   `hardMax`: the domain will not go above this value (e.g. 0 for non-positive data).
 *   When a constraint is violated after nicing/padding, the domain is clamped and re-niced.
 * @returns Object with nice min/max boundaries and explicit tick values
 *
 * @example
 * ```ts
 * // Data range: 127 to 843
 * const result = calculateNiceDomainWithTicks(127, 843, 5);
 * // Returns: { niceMin: 100, niceMax: 900, tickValues: [100, 300, 500, 700, 900] }
 *
 * // Non-negative data: prevent nicing from crossing below zero
 * const constrained = calculateNiceDomainWithTicks(5, 95, 5, undefined, { hardMin: 0 });
 * // niceMin will be >= 0
 * ```
 */
export function calculateNiceDomainWithTicks(
  min: number,
  max: number,
  targetTicks: number = 5,
  referenceLinePoints?: number[],
  constraints?: { hardMin?: number; hardMax?: number },
): { niceMin: number; niceMax: number; tickValues: number[] } {
  // Validate targetTicks: must be at least 2 to create a meaningful scale
  const validatedTargetTicks = Math.max(2, Math.floor(targetTicks));

  const range = max - min;

  // Handle zero range case (empty data, single value, or all identical values)
  if (range === 0) {
    if (min === 0) {
      // If the single value is 0, use a small default range.
      // Using 1 keeps the axis tight around zero (e.g., 0–100% for percentage data
      // instead of 0–1000% with the previous default of 10).
      const niceMax = 1;
      const step = niceMax / (validatedTargetTicks - 1);
      const tickValues: number[] = [];
      for (let i = 0; i < validatedTargetTicks; i++) {
        tickValues.push(Math.round(i * step * 1e10) / 1e10);
      }
      return { niceMin: 0, niceMax, tickValues };
    } else {
      // If the single value is non-zero, create a symmetric range around it
      const absValue = Math.abs(min);
      const magnitude = Math.floor(Math.log10(absValue));
      const powerOf10 = Math.pow(10, magnitude);

      const rangeSize = powerOf10 * 2;
      const niceMin = Math.floor(min / powerOf10) * powerOf10 - powerOf10;
      const niceMax = niceMin + rangeSize;

      const step = rangeSize / (validatedTargetTicks - 1);
      const tickValues: number[] = [];
      for (let i = 0; i < validatedTargetTicks; i++) {
        tickValues.push(Math.round((niceMin + i * step) * 1e10) / 1e10);
      }
      return { niceMin, niceMax, tickValues };
    }
  }

  // Calculate approximate step size to get close to targetTicks
  const roughStep = range / (validatedTargetTicks - 1);

  // Round roughStep to a nice number (1, 2, 5, 10, 20, 50, etc.)
  const magnitude = Math.floor(Math.log10(roughStep));
  const powerOf10 = Math.pow(10, magnitude);
  const normalized = roughStep / powerOf10;

  // Round to nearest nice number: 1, 2, 5, or 10
  let niceFactor: number;
  if (normalized <= 1.5) {
    niceFactor = 1;
  } else if (normalized <= 3) {
    niceFactor = 2;
  } else if (normalized <= 7) {
    niceFactor = 5;
  } else {
    niceFactor = 10;
  }

  const step = niceFactor * powerOf10;

  // Round min DOWN to nearest step, max UP to nearest step
  let niceMin = Math.floor(min / step) * step;
  let niceMax = Math.ceil(max / step) * step;

  // Pad domain when horizontal reference lines sit on (or very near) a boundary,
  // so the line isn't invisible at the edge. Only applies to ReferenceLine points,
  // not ReferenceArea bounds — areas at edges are intentional (e.g. web vitals bands).
  if (referenceLinePoints && referenceLinePoints.length > 0) {
    const tolerance = step * 0.1;
    const needsLowerPadding = referenceLinePoints.some((p) => p <= niceMin + tolerance);
    const needsUpperPadding = referenceLinePoints.some((p) => p >= niceMax - tolerance);

    if (needsLowerPadding || needsUpperPadding) {
      if (needsLowerPadding) {
        niceMin -= step;
      }
      if (needsUpperPadding) {
        niceMax += step;
      }
      return calculateNiceDomainWithTicks(niceMin, niceMax, targetTicks, undefined, constraints);
    }
  }

  // Clamp to hard constraints when nicing/padding pushed the domain past a bound.
  // Re-nice after clamping so ticks stay evenly spaced within the new range.
  if (constraints) {
    let needsRecalc = false;
    if (constraints.hardMin !== undefined && niceMin < constraints.hardMin) {
      niceMin = constraints.hardMin;
      needsRecalc = true;
    }
    if (constraints.hardMax !== undefined && niceMax > constraints.hardMax) {
      niceMax = constraints.hardMax;
      needsRecalc = true;
    }
    if (needsRecalc) {
      return calculateNiceDomainWithTicks(niceMin, niceMax, targetTicks);
    }
  }

  // Generate explicit ticks from niceMin to niceMax with the step
  const numSteps = Math.round((niceMax - niceMin) / step);
  const tickValues: number[] = [];
  for (let i = 0; i <= numSteps; i++) {
    const tick = niceMin + i * step;
    tickValues.push(Math.round(tick * 1e10) / 1e10);
  }

  return { niceMin, niceMax, tickValues };
}

/**
 * Computes the stacked Y maximum for column and stacked-area charts.
 *
 * The baseline is always 0 — column charts and stacked areas should never
 * render below zero. For each data point we sum the positive series values
 * and track the overall maximum.
 *
 * @param data - Array of chart data points
 * @param seriesDataKeys - Array of dataKeys from BarSeries / AreaStackSeries components
 * @returns The maximum stacked Y value (minimum is always 0)
 *
 * @example
 * ```ts
 * const data = [
 *   { x: 1, cpu: 20, memory: 30 },
 *   { x: 2, cpu: 40, memory: 50 },
 * ];
 * computeStackedYMax(data, ['cpu', 'memory']);
 * // Returns: 90
 * ```
 */
export function computeStackedYMax<T extends Record<string, unknown>>(
  data: T[] | null | undefined,
  seriesDataKeys: string[],
): number {
  if (!data || data.length === 0 || seriesDataKeys.length === 0) {
    return 0;
  }

  let maxSum = 0;

  for (const point of data) {
    let sum = 0;

    for (const dataKey of seriesDataKeys) {
      const value = point[dataKey];
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        sum += value;
      }
    }

    maxSum = Math.max(maxSum, sum);
  }

  return maxSum;
}
